import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";

import { WeddingCalculatorHeader } from "./calculator/WeddingCalculatorHeader";
import { GuestsCard } from "./calculator/GuestsCard";
import { PerPersonCard } from "./calculator/PerPersonCard";
import { FixedCostsCard } from "./calculator/FixedCostsCard";
import { SummaryPanel } from "./calculator/SummaryPanel";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

function WeddingCalculator() {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const [guests, setGuests] = useState("");
  const [costPerPerson, setCostPerPerson] = useState("");
  const [churchPriest, setChurchPriest] = useState("");
  const [churchFlowers, setChurchFlowers] = useState("");
  const [decorations, setDecorations] = useState("");
  const [showChurchPriest, setShowChurchPriest] = useState(true);
  const [showChurchFlowers, setShowChurchFlowers] = useState(true);
  const [showDecorations, setShowDecorations] = useState(true);
  const [customItems, setCustomItems] = useState([]);
  const [customPerPersonItems, setCustomPerPersonItems] = useState([]);
  const [code, setCode] = useState("");
  const [loadCode, setLoadCode] = useState("");
  const [initialCodeLoaded, setInitialCodeLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const nGuests = Number(guests) || 0;

  const formatMoneyValue = (value) => {
    const num = Number(value);
    return num > 0 ? String(num) : "";
  };

  const formatWithSpaces = (value) => {
    const num = Math.round(Number(value) || 0);
    return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const handleAddItem = () => {
    setCustomItems([...customItems, { label: "", amount: "" }]);
  };

  const handleRemoveItem = (index) => {
    setCustomItems(customItems.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...customItems];
    updated[index] = { ...updated[index], [field]: value };
    setCustomItems(updated);
  };

  const handleAddPerPersonItem = () => {
    setCustomPerPersonItems([
      ...customPerPersonItems,
      { label: "", amount: "" },
    ]);
  };

  const handleRemovePerPersonItem = (index) => {
    setCustomPerPersonItems(customPerPersonItems.filter((_, i) => i !== index));
  };

  const handlePerPersonItemChange = (index, field, value) => {
    const updated = [...customPerPersonItems];
    updated[index] = { ...updated[index], [field]: value };
    setCustomPerPersonItems(updated);
  };

  const liveSummary = useMemo(() => {
    const baseCostPer = Number(costPerPerson) || 0;
    const priest = showChurchPriest ? Number(churchPriest) || 0 : 0;
    const flowers = showChurchFlowers ? Number(churchFlowers) || 0 : 0;
    const decor = showDecorations ? Number(decorations) || 0 : 0;

    const items = [];

    const totalPerPersonCost =
      baseCostPer +
      customPerPersonItems.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    const partyCost = totalPerPersonCost * nGuests;
    if (partyCost > 0)
      items.push({ label: t("items.guestsPerPerson"), amount: partyCost });

    if (priest > 0)
      items.push({ label: t("items.churchPriest"), amount: priest });
    if (flowers > 0)
      items.push({ label: t("items.churchFlowers"), amount: flowers });
    if (decor > 0) items.push({ label: t("items.decorations"), amount: decor });

    customItems.forEach((i) => {
      const amt = Number(i.amount) || 0;
      if (!i.label && !amt) return;
      items.push({ label: i.label || t("items.defaultCustom"), amount: amt });
    });

    const total = items.reduce((sum, i) => sum + i.amount, 0);
    const perGuest = nGuests ? total / nGuests : 0;

    return { total, perGuest, items };
  }, [
    nGuests,
    costPerPerson,
    churchPriest,
    churchFlowers,
    decorations,
    showChurchPriest,
    showChurchFlowers,
    showDecorations,
    customItems,
    customPerPersonItems,
    t,
  ]);

  // Load config automatically if URL contains /calculator/:code
  useEffect(() => {
    if (!initialCodeLoaded && params.code) {
      const trimmed = String(params.code).trim();
      if (trimmed) {
        setLoadCode(trimmed);
        // Fire and forget; loadConfig uses current loadCode, so call directly with value
        (async () => {
          await loadConfig(trimmed);
        })();
      }
      setInitialCodeLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCodeLoaded, params.code]);

  const saveConfiguration = async (options) => {
    if (liveSummary.items.length === 0) {
      console.log("saveConfiguration: no items in summary, aborting");
      return;
    }
    setError("");
    setLoading(true);
    console.log("saveConfiguration: starting", { options, liveSummary });
    try {
      const payload = {
        items: liveSummary.items.map((i) => ({
          label: i.label,
          amount: Number(i.amount) || 0,
        })),
        total_cost: liveSummary.total,
        guests: nGuests,
        cost_per_person: Number(costPerPerson) || 0,
        church_priest: Number(churchPriest) || 0,
        church_flowers: Number(churchFlowers) || 0,
        decorations: Number(decorations) || 0,
        custom_items: customItems.map((i) => ({
          label: i.label,
          amount: Number(i.amount) || 0,
        })),
        custom_per_person_items: customPerPersonItems.map((i) => ({
          label: i.label,
          amount: Number(i.amount) || 0,
        })),
        language: i18n.language || i18n.resolvedLanguage || "en",
      };

      const res = await fetch(`${API_BASE_URL}/generate-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      console.log("saveConfiguration: response status", res.status);
      if (!res.ok) throw new Error(t("api.errors.saveFailed"));

      const configCode = res.headers.get("X-Wedding-Code");
      console.log(
        "saveConfiguration: received header X-Wedding-Code =",
        configCode,
      );
      if (configCode) {
        setCode(configCode);
      }

      if (options.downloadPdf) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "wedding-summary.pdf";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadConfig = async (explicitCode) => {
    const fallback = typeof loadCode === "string" ? loadCode : "";
    const raw = explicitCode != null ? explicitCode : fallback;
    const codeToLoad = typeof raw === "string" ? raw.trim() : "";
    if (!codeToLoad) return;
    // use codeToLoad above
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/config/${codeToLoad}`);
      if (!res.ok) throw new Error(t("api.errors.configNotFound"));
      const data = await res.json();

      console.log("loadConfig: loaded data", data);

      // Reset and restore full configuration from backend snapshot
      setGuests(String(data.guests ?? ""));
      setCostPerPerson(String(data.cost_per_person ?? ""));
      setChurchPriest(String(data.church_priest ?? ""));
      setChurchFlowers(String(data.church_flowers ?? ""));
      setDecorations(String(data.decorations ?? ""));
      setShowChurchPriest(true);
      setShowChurchFlowers(true);
      setShowDecorations(true);

      setCustomPerPersonItems(
        (data.custom_per_person_items || []).map((i) => ({
          label: i.label,
          amount: String(i.amount ?? ""),
        })),
      );

      setCustomItems(
        (data.custom_items || []).map((i) => ({
          label: i.label,
          amount: String(i.amount ?? ""),
        })),
      );

      if (data.code) {
        setCode(data.code);
        setLoadCode(data.code);
        // Normalise route: ensure /calculator/:code URL is used when a code exists
        try {
          navigate(`/calculator/${data.code}`, { replace: true });
        } catch (e) {
          // ignore navigation errors in non-router environments
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex flex-col gap-6 py-6 text-slate-800">
      <section className="max-w-2xl space-y-3">
        <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight text-slate-900">
          {t("home.cards.calculator.title", "Wedding cost calculator")}
        </h2>
        <p className="text-base text-slate-600 max-w-xl">
          {t(
            "home.cards.calculator.description",
            "Estimate your total wedding budget in minutes — fixed costs, per‑person spending, and a clear summary in one place.",
          )}
        </p>
      </section>

      <div className="grid gap-6 md:grid-cols-[minmax(0,3fr)_minmax(260px,2fr)]">
        <div className="flex flex-col gap-4">
          <WeddingCalculatorHeader t={t} />

          <GuestsCard guests={guests} onChange={setGuests} t={t} />

          <PerPersonCard
            t={t}
            costPerPerson={costPerPerson}
            onCostPerPersonChange={setCostPerPerson}
            customPerPersonItems={customPerPersonItems}
            onAddItem={handleAddPerPersonItem}
            onRemoveItem={handleRemovePerPersonItem}
            onItemChange={handlePerPersonItemChange}
            formatMoneyValue={formatMoneyValue}
          />

          <FixedCostsCard
            t={t}
            churchPriest={churchPriest}
            onChurchPriestChange={setChurchPriest}
            churchFlowers={churchFlowers}
            onChurchFlowersChange={setChurchFlowers}
            decorations={decorations}
            onDecorationsChange={setDecorations}
            showChurchPriest={showChurchPriest}
            setShowChurchPriest={setShowChurchPriest}
            showChurchFlowers={showChurchFlowers}
            setShowChurchFlowers={setShowChurchFlowers}
            showDecorations={showDecorations}
            setShowDecorations={setShowDecorations}
            customItems={customItems}
            onAddItem={handleAddItem}
            onRemoveItem={handleRemoveItem}
            onItemChange={handleItemChange}
            formatMoneyValue={formatMoneyValue}
          />
        </div>

        <SummaryPanel
          t={t}
          liveSummary={liveSummary}
          nGuests={nGuests}
          formatWithSpaces={formatWithSpaces}
          loading={loading}
          error={error}
          code={code}
          loadCode={loadCode}
          setLoadCode={setLoadCode}
          onReset={() => {
            setGuests("");
            setCostPerPerson("");
            setChurchPriest("");
            setChurchFlowers("");
            setDecorations("");
            setShowChurchPriest(true);
            setShowChurchFlowers(true);
            setShowDecorations(true);
            setCustomItems([]);
            setCustomPerPersonItems([]);
            setCode("");
            setError("");
          }}
          onSaveConfig={saveConfiguration}
          onLoadConfig={loadConfig}
        />
      </div>
    </div>
  );
}

export default WeddingCalculator;
